import { useState, useEffect } from "react";
import { BarChart3, RefreshCw, Download } from "lucide-react";
import axios, { API } from "@/utils/api";
import { useT, useDeptName } from "@/contexts/LanguageContext";

const ResidentialConsolidatedView = ({ clientId, consolidated, summary, onRefresh }) => {
  const t = useT();
  const dn = useDeptName();
  const [downloading, setDownloading] = useState(false);

  const handleDownloadExcel = async () => {
    setDownloading(true);
    try {
      const tokenRes = await axios.post(`${API}/clients/${clientId}/download-token`);
      const url = `${API}/clients/${clientId}/residential/export/excel?token=${tokenRes.data.token}`;
      const a = document.createElement("a");
      a.href = url;
      a.setAttribute("download", "Residential_Report.xlsx");
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (error) {
      console.error("Error downloading Excel:", error);
    } finally {
      setDownloading(false);
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "completed": return "badge-success";
      case "in_progress": return "badge-warning";
      default: return "badge-neutral";
    }
  };

  return (
    <div data-testid="residential-consolidated-view">
      <div className="page-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap" }}>
        <div>
          <h2 className="page-title">{t("residential_consolidated")}</h2>
          <p className="page-subtitle">Consolidated results across all residential audit categories.</p>
        </div>
        <div style={{ display: "flex", gap: "8px" }}>
          <button className="btn btn-primary btn-sm" onClick={handleDownloadExcel} disabled={downloading} data-testid="download-res-excel-btn">
            <Download size={16} /> {downloading ? "Downloading..." : t("download_excel")}
          </button>
          <button className="btn btn-secondary btn-sm" onClick={onRefresh} data-testid="res-consolidated-refresh">
            <RefreshCw size={16} /> Refresh
          </button>
        </div>
      </div>

      {summary && (
        <div className="stats-grid mb-lg">
          <div className="stat-card">
            <div className="stat-value">{summary.total_departments}</div>
            <div className="stat-label">Total Categories</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{summary.audits_completed}</div>
            <div className="stat-label">Audits Completed</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{summary.applicable_departments}</div>
            <div className="stat-label">Applicable</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{summary.total_co2_kg?.toFixed(1)} kg</div>
            <div className="stat-label">Total CO2e</div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <h4 className="card-title">
            <BarChart3 size={18} style={{ marginRight: "8px", verticalAlign: "middle" }} />
            Categories Summary
          </h4>
        </div>
        {consolidated.length === 0 ? (
          <div className="flex-center" style={{ padding: "var(--space-xxl)", color: "var(--text-muted)" }}>
            <p>No residential audit data yet. Start auditing categories to see results.</p>
          </div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Category</th>
                  <th>Status</th>
                  <th>Completion</th>
                  <th>CO2 Emissions (kg)</th>
                </tr>
              </thead>
              <tbody>
                {consolidated.map((dept) => (
                  <tr key={dept.key}>
                    <td style={{ fontWeight: "600" }}>{dn(dept.key)}</td>
                    <td><span className={`badge ${getStatusBadgeClass(dept.status)}`}>{t(dept.status)}</span></td>
                    <td>
                      <div className="progress-bar" style={{ width: "120px", display: "inline-block" }}>
                        <div
                          className={`progress-fill ${dept.status === "completed" ? "success" : "warning"}`}
                          style={{ width: `${dept.completion_percentage}%` }}
                        ></div>
                      </div>
                      <span className="text-muted" style={{ marginLeft: "8px" }}>{Math.round(dept.completion_percentage)}%</span>
                    </td>
                    <td>{dept.co2_emissions?.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResidentialConsolidatedView;
